import { useEffect } from 'react';

import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
import { Routes, createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';

import { applyAxiosContextToEvent } from './sentryEventContext';

type CaptureContext = Parameters<typeof Sentry.captureException>[1];
type Breadcrumb = Parameters<typeof Sentry.addBreadcrumb>[0];
type SentryUser = Parameters<typeof Sentry.setUser>[0];

const isProduction = import.meta.env.VITE_ENVIRONMENT === 'production';

const SENTRY_DSN = 'https://5a7d5020cbc34f8e89b975eca79a30fc@o4504777243885568.ingest.sentry.io/4504777251553280';

const initSentry = (): void => {
  if (!isProduction) {
    // eslint-disable-next-line no-console
    console.warn('Sentry not initialized');
    return;
  }

  try {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: import.meta.env.VITE_ENVIRONMENT,
      release: import.meta.env.VITE_VERSION,
      attachStacktrace: true,
      ignoreErrors: [
        'ChunkLoadError',
        'Loading chunk',
        'Failed to fetch dynamically imported module',
        'Importing a module script failed',
        'error loading dynamically imported module',
      ],
      integrations: [
        new BrowserTracing({
          routingInstrumentation: Sentry.reactRouterV6Instrumentation(
            useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
          ),
        }),
      ],
      beforeSend: (event, hint) => {
        try {
          applyAxiosContextToEvent(event as never, hint?.originalException);
        } catch {
          // Keep the original event if enrichment fails.
        }
        return event;
      },
    });
    Sentry.setTag('app.version', import.meta.env.VITE_VERSION);
  } catch {
    // Init must never block the app.
  }
};

export const captureException = (error: unknown, captureContext?: CaptureContext): void => {
  if (!isProduction) {
    // eslint-disable-next-line no-console
    console.error('[Sentry]', error, captureContext);
    return;
  }
  try {
    Sentry.captureException(error, captureContext);
  } catch {
    // Reporting must never break the app.
  }
};

export const addBreadcrumb = (breadcrumb: Breadcrumb): void => {
  if (!isProduction) return;
  try {
    Sentry.addBreadcrumb(breadcrumb);
  } catch {
    // Reporting must never break the app.
  }
};

export const setSentryUser = (user: SentryUser | null): void => {
  if (!isProduction) return;
  try {
    Sentry.setUser(user);
  } catch {
    // Reporting must never break the app.
  }
};

export const setSentryContext = (name: string, context: Record<string, unknown> | null): void => {
  if (!isProduction) return;
  try {
    Sentry.setContext(name, context);
  } catch {
    // Reporting must never break the app.
  }
};

/** Parameterized route names on Sentry events (`/auth/password-reset/:token/:uidb`). */
export const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

export default initSentry;
